<script setup>
/*
    GaugeWidget.vue - Gauge directive for creating animated gauges.

    This component renders a gauge using an HTML5 canvas. It supports animation for value changes,
    customizable appearance through CSS variables, and dynamic scaling of the gauge's range.

    Components:
        primary: <canvas> - The main canvas element for rendering the gauge.

    Widget Properties:
        format - Number formatting options for the displayed value.
        max - Maximum value for the gauge scale (default: 100).
        min - Minimum value for the gauge scale (default: 0).
        range - Animation range settings.
        text - Label text to display below the value.
        value - Current numeric value to display.

    CSS Variables:
        background: The background color of the gauge (defaults to --v-theme-background-lighten-1).
        --w-fill-color: The fill color of the gauge arc (defaults to --v-theme-primary with opacity).
        --w-text-color: The color for text elements like value and labels (defaults to --v-theme-text).
        --w-needle-color: The color of the gauge's needle (defaults to --v-theme-text-darken-1).
        --w-units: Optional units to display next to the value.
        --w-max: Overrides the 'max' property for the gauge's scale.
        --w-min: Overrides the 'min' property for the gauge's scale.
 */
import {reactive, ref} from 'vue'
import {clone, delay, waitRender} from '@/paks/vu-app'
import {formatValue} from '@/paks/js-format'
import {getColor, getProp, getWidgetProps, lighten} from '@/paks/vu-dash/BoardUtils'

defineExpose({update})

const props = defineProps({
    widget: Object,
})

const page = reactive({
    args: {}, // Component arguments
    animatedValue: 0, // The animated value of the gauge
    configuring: false, // True when configuring the gauge
    data: {}, // Widget.value data
    frame: FRAME_RATE, // Animation frame rate
    height: null, // Height of the gauge canvas
    min: null, // Minimum value of the gauge (from theme or dynamically computed)
    max: null, // Maximum value of the gauge
    period: props.widget.range?.period || PERIOD, // Animation duration in seconds
    styles: {}, // Computed styles
    theme: {}, // Theme colors
    updating: null, // True when updating the gauge
    width: null, // Width of the gauge canvas
    value: null, // The current value of the gauge
    x: 0, // X position of the gauge
    y: 0, // Y position of the gauge
})

//  Component references
const primary = ref(null)

const PERIOD = 5 // Default animation duration in seconds
const FRAME_RATE = 10 // Animation frame rate
const MIN_SIZE = 5 // Minimum size of the gauge in pixels

/*
    Update the drawn value. Changes are animiated.
 */
async function update(params = {}) {
    let widget = clone(props.widget)
    let vel = primary.value

    // Configure canvas on initialization or if dimensions are not set
    if (params.initialize || vel?.height == 0 || !page.width || !page.height) {
        configure()
    }

    Object.assign(page, getWidgetProps(widget, {primary}))
    page.theme = makeTheme()

    let value = (page.value = boundValue(page.data.primary || 0))
    // Exit early if canvas unavailable, already updating, invalid value, or page hidden
    if (!vel || page.updating || isNaN(value) || hidden()) {
        return
    }
    page.updating = true

    //  Save value to skip rendering on subsequent updates to the same value
    let steps = Math.min(page.period * page.frame, 10)
    if (page.animatedValue > page.max) {
        page.animatedValue = page.max
    } else if (!page.animatedValue) {
        steps = 1
    }
    let originalDiff = value - page.animatedValue
    let minDiff = Math.abs(originalDiff / 100)
    let count = 25

    // Animate the value change
    do {
        let diff = value - page.animatedValue
        let inc = diff / steps
        page.animatedValue += inc < 0 ? inc : inc

        draw(value, page.animatedValue)

        diff = value - page.animatedValue
        if (Math.abs(diff) <= minDiff) {
            break
        }
        await delay(250 / page.frame)
        // This loop continues as long as the canvas is present, the target value hasn't changed,
        // and a maximum number of animation frames has not been reached.
    } while (vel && value == page.value && count-- > 0)

    page.animatedValue = value
    page.updating = false
    await waitRender()
    if (value != page.value) {
        update(page.value)
    }
}

/*
    Configure the gauge dimensions and canvas.
    This function calculates the drawing area and sets up the canvas for rendering.
*/
function configure() {
    let vel = primary.value
    if (!vel || page.configuring) {
        return
    }
    page.configuring = true

    /*
        Calculate a maximal square with padding
     */
    let c = vel.getContext('2d')
    let widgetElement = primary.value
    let {offsetHeight, offsetWidth} = widgetElement

    page.square = Math.max(Math.min(offsetHeight, offsetWidth), MIN_SIZE)
    page.square = parseInt(page.square * 0.9)
    page.height = page.width = page.square
    page.x = (offsetWidth - page.square) / 2
    page.y = (offsetHeight - page.square) / 2

    if (props.widget.header) {
        page.y += Math.max(10, parseInt(page.square / 40))
    }
    c.canvas.width = offsetWidth
    c.canvas.height = offsetHeight
    c.clearRect(0, 0, vel.width, vel.height)
    page.configuring = false
}

/*
    Create a theme object by reading CSS custom properties and widget properties.
    This allows for dynamic styling of the gauge.
    @returns {Object} A theme object with colors, min/max values, and units.
*/
function makeTheme() {
    let backgroundColor = getColor(page, 'background', '--v-theme-background-lighten-1', {
        opacity: 1,
    })
    let fillColor = getColor(page, '--w-fill-color', '--v-theme-primary', {_opacity: 0.35})
    let textColor = getColor(page, '--w-text-color', '--v-theme-text')
    let needleColor = getColor(page, '--w-needle-color', '--v-theme-text-darken-1')
    let units = getProp(page, '--w-units', '')
    let max = getProp(page, '--w-max', props.widget.max)
    let min = getProp(page, '--w-min', props.widget.min)

    let theme = {
        background: backgroundColor,
        fillColor: fillColor,
        max: max,
        min: min,
        needleColor: needleColor,
        textColor: textColor,
        units: units,
    }
    page.min = min || 0
    page.max = max
    return theme
}

/*
    Draw the gauge on the canvas.
    This function renders the gauge arc, text, and needle based on the current and animated values.
    @param {number} value The current target value of the gauge.
    @param {number} animatedValue The current value to display during animation.
 */
function draw(value, animatedValue) {
    let vel = primary.value
    if (!vel) {
        return
    }
    let theme = page.theme

    let c = vel.getContext('2d')
    let styles = page.styles
    let height = parseInt(page.height)
    let width = parseInt(page.width)
    let radius = page.square * 0.55
    let arrowLength = radius * 1.1
    let sweep = 0.65 * Math.PI
    let startAngle = 1.5 * Math.PI - sweep / 2
    let endAngle = 1.5 * Math.PI + sweep / 2

    let font = styles.getPropertyValue('font-family') || 'Inter'
    let fontSize = Math.round(12 + Math.max(height - 200, 0) * 0.06)

    if (radius <= 1) {
        radius = 1
    }
    // Clear and fill background
    c.save()
    c.fillStyle = theme.background
    c.clearRect(0, 0, vel.width, vel.height)
    c.fillRect(0, 0, vel.width, vel.height)

    let y = page.y + height * 0.65

    //  Gauge max, min
    c.font = '' + fontSize + 'px ' + font
    c.fillStyle = theme.textColor
    c.textAlign = 'right'
    c.fillText('' + Math.round(page.max - 0).toLocaleString(), page.x + width, y)

    c.textAlign = 'left'
    c.fillText('' + Math.round(page.min - 0).toLocaleString(), page.x, y)
    y += fontSize / 2

    //  Gauge background arc
    let x = page.x + width / 2
    let cx = x
    let cy = y
    c.beginPath()
    c.moveTo(cx, cy)
    c.arc(cx, cy, radius, startAngle, endAngle, false)
    let g = c.createLinearGradient(0, cy, width, cy + arrowLength)
    g.addColorStop(0, lighten(theme.fillColor, 45))
    g.addColorStop(1, lighten(theme.fillColor, 0))
    c.fillStyle = g
    c.closePath()
    c.fill()

    // Center circle cutout
    c.beginPath()
    c.moveTo(cx, cy)
    c.fillStyle = theme.background
    c.arc(cx, cy, radius * 0.3, 0, 2 * Math.PI, false)
    c.closePath()
    c.fill()
    y += fontSize * 2.3

    //  Gauge value text
    c.font = '' + Math.round(fontSize * 1.6) + 'px ' + font
    c.textAlign = 'center'
    c.fillStyle = theme.textColor
    let valueText = formatValue(value, {format: props.widget.format})
    c.fillText(valueText, cx, y)

    // Units text
    if (theme.units) {
        c.font = fontSize + 'px ' + font
        c.fillStyle = theme.textColor
        c.textAlign = 'left'
        let textWidth = c.measureText(value.toLocaleString()).width
        c.fillText(`/ ${theme.units}`, x + textWidth, y)
    }
    y += fontSize * 2

    /*
        Draw the gauge arrow/needle.
        @param {number} angle The angle of the arrow in radians, normalized from -1 to 1.
    */
    function arrow(angle) {
        if (angle < -1) {
            angle = -1
        } else if (angle > 1) {
            angle = 1
        }
        cy -= 4
        c.save()
        c.beginPath()
        c.translate(cx, cy)
        c.rotate(angle)

        // Arrow shape
        c.moveTo(0, -arrowLength)
        c.lineTo(4, 0)
        c.lineTo(-4, 0)
        c.lineTo(0, -arrowLength)
        c.closePath()
        c.fillStyle = theme.needleColor
        c.fill()

        //  Arrow base circle
        c.beginPath()
        c.arc(0, 0, 4, 0, 2 * Math.PI, false)
        c.fillStyle = theme.needleColor
        c.closePath()
        c.fill()
        c.translate(-cx, -cy)
        c.restore()
    }
    animatedValue = Math.min(animatedValue, page.max)
    // Normalize the animated value to a percentage of the max value.
    animatedValue = (animatedValue / page.max) * 100
    // Convert the percentage to an angle for the arrow.
    arrow(((animatedValue - 50) / 50) * (sweep / 2))
    c.restore()
}

/* 
    Get the next power of 10 for a given number.
    @param {number} n The number to find the next power of 10 for.
    @returns {number} The next power of 10. 
*/
function nextPowerOf10(n) {
    if (n <= 0) return 1
    const exponent = Math.ceil(Math.log10(n))
    return Math.pow(10, exponent)
}

/* 
   Set the min and max values for the gauge.
   If max is not defined via theme or props, it is set to the next power of 10 of the current value.
   This allows the gauge to dynamically adjust its scale.
   @param {number} v The current value.
*/
function setMinMax(v) {
    let theme = page.theme
    if (!theme.max) {
        // Max not defined. Set to next power of 10
        if (v > page.max || page.max == null) {
            page.max = nextPowerOf10(v)
        }
    } else {
        page.max = theme.max
    }
    page.min = (theme.min ? theme.min : props.widget.min) || 0
}

/*
    Bound a value to be within the min and max of the gauge.
    It also triggers setting the min/max values.
    @param {number} v The value to bound.
    @returns {number} The bounded value.
*/
function boundValue(v) {
    setMinMax(v)
    let value = v - 0
    if (value < page.min) {
        value = 0
    }
    if (value > page.max) {
        value = page.max
    }
    return value
}

/*
    Check if the document is hidden.
    Used to prevent animations when the tab is not visible, saving resources.
    @returns {boolean} True if the document is hidden.
 */
function hidden() {
    return document.hidden || document.msHidden || document.webkitHidden || document.mozHidden
}
</script>

<template>
    <canvas class="gauge-widget" ref="primary" v-bind="page.args">
        This text is displayed if your browser does not support HTML5 Canvas.
    </canvas>
</template>

<style lang="scss">
/* Styles for the gauge widget container */
.gauge-widget {
    height: 100%;
    width: 100%;
}
</style>
